'use strict';
import get from 'lodash/get';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/merge';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/takeUntil';
import {Observable} from 'rxjs/Observable';
import {getOrganization, getOrgProfile} from '../../app/model/reducers/state';
import {SCOPE_DESTROY} from '../../config/constants/view-events';
import {getAsString, getCollection, getId} from '../../core-utilities/domain/ReduxUtils';
import {eventToObservable, observeNewOnScope} from '../../core-utilities/rx-utils';
import {PROJECT_COLLECTION} from '../../projects/domain/ProjectDomain';


ManageAccessModalController.$inject = [
  '$scope',
  '$modal',
  '$popup',
  '$translate',
  '$miscUtils',
  '$ngRedux',
  '$permissions',
  'rxPermissionsService',
  'selectorsService',
  'NotificationService',
  'ProjectDomain',
  'PersonDomain',
  'TeamDomain',
  'TaskDomain',
  'AssetTaskDomain',
  'DEFAULT_ROLES',
  'targetDomain',
];

export function ManageAccessModalController(
  $scope,
  $modal,
  $popup,
  $translate,
  $miscUtils,
  $ngRedux,
  $permissions,
  rxPermissionsService,
  selectorsService,
  NotificationService,
  ProjectDomain,
  PersonDomain,
  TeamDomain,
  TaskDomain,
  AssetTaskDomain,
  DEFAULT_ROLES,
  targetDomain,
) {
  /*****************************************************************************************************************
   * Initialize
   ****************************************************************************************************************/

  const vm = $scope.manageAccess = this;
  let permission = getEditPermission();
  vm.isPrivate = null;
  vm.roles = DEFAULT_ROLES.toList();
  vm.roleLinks = DEFAULT_ROLES.toLinksList();
  vm.toAdd = {
    role: 'user',
  };

  /* Controller Actions */
  vm.promptUpdate = promptUpdate;
  vm.addMember = addMember;
  vm.selectPersonnel = selectPersonnel;
  vm.reset = reset;
  vm.removeMember = $miscUtils.blockPromise(removeMember);
  vm.close = $modal.cancel.bind($modal);

  /* Presentation Logic */
  vm.canEditUser = canEditUser;
  vm.showAddButton = showAddButton;
  vm.showCancelButton = showCancelButton;

  observeNewOnScope($scope, () => targetDomain.isPrivate || false)
    .distinctUntilChanged()
    .switchMap(onIsPrivateUpdate)
    .takeUntil(eventToObservable($scope, SCOPE_DESTROY))
    .subscribe(nextState => $scope.$evalAsync(() => Object.assign(vm, nextState)));

  function onIsPrivateUpdate(isPrivate) {
    if (vm.isPrivate !== null) isPrivate ? cleanupPrivate() : cleanupPublic();
    vm.isPrivate = isPrivate;
    return isPrivate ? configurePrivate() : configurePublic();
  }


  /*****************************************************************************************************************
   * Controller Actions
   ****************************************************************************************************************/

  let addingInProgress = false; // needed to also hide the cancel button
  function addMember() {
    if (!vm.toAdd.target || !vm.toAdd.role) {
      return Promise.resolve(null);
    } else {
      let promise;
      switch (getCollection(vm.toAdd.target)) {
        case PersonDomain.COLLECTION:
          promise = getTargetDomainClass(targetDomain).changePersonRole(
            getId(targetDomain),
            getId(vm.toAdd.target),
            vm.toAdd.role,
          );
          break;
        case TeamDomain.COLLECTION:
          promise = getTargetDomainClass(targetDomain).changeTeamRole(
            getId(targetDomain),
            getId(vm.toAdd.target),
            vm.toAdd.role,
          );
          break;
        default:
          return Promise.reject("Invalid state!");
      }

      addingInProgress = true;
      return promise
        .then(() => reset())
        .finally(() => addingInProgress = false);
    }
  }

  function removeMember(target) {
    if (!target) {
      return Promise.resolve(null);
    } else {
      let promise;
      switch (getCollection(target)) {
        case PersonDomain.COLLECTION:
          promise = getTargetDomainClass(targetDomain).removePersonRole(
            getId(targetDomain),
            getId(target),
          );
          break;
        case TeamDomain.COLLECTION:
          promise = getTargetDomainClass(targetDomain).removeTeamRole(
            getId(targetDomain),
            getId(target),
          );
          break;
        default:
          return Promise.reject("Invalid state!");
      }

      return promise.catch(notifyOfFailure);
    }

    function notifyOfFailure(e) {
      // TODO: incorporate error?
      $translate('default.roles.remove.error', {
        profile: getAsString(target),
        name: getAsString(targetDomain),
      }).then(msg => NotificationService.reportError({
        removal: NotificationService.REMOVAL.NEVER,
        message: msg,
      }));
    }
  }

  function selectPersonnel() {
    const assignment = vm.toAdd.target || null;

    return selectorsService
      .selectPersonnel(assignment, null, null, {nonUsers: true})
      .then(onSelect);
  }

  function onSelect(person) {
    vm.toAdd.target = person;
  }

  function reset() {
    vm.toAdd = {
      role: 'user',
    };
  }

  function promptUpdate(target, role, oldRole) {
    if (target && role) {
      Promise
        .props({
          title: $translate('organizations.roles.change.confirmation.title'),
          msg: $translate('organizations.roles.change.confirmation', {
            profile: getAsString(target),
            role: vm.roleLabels[role],
          }),
        })
        .then(messages =>
          // TODO(i18n): convert to confirmI18n method when created
          $popup.confirm({
            title: messages.title,
            template: messages.msg,
          })
        )
        .then(result => {
          if (result) {
            let promise;
            switch (getCollection(target)) {
              case PersonDomain.COLLECTION:
                promise = getTargetDomainClass(targetDomain).changePersonRole(
                  getId(targetDomain),
                  getId(target),
                  role,
                );
                break;
              case TeamDomain.COLLECTION:
                promise = getTargetDomainClass(targetDomain).changeTeamRole(
                  getId(targetDomain),
                  getId(target),
                  role,
                );
                break;
              default:
                return Promise.reject("Invalid state!");
            }

            promise.catch(notifyOfFailure);
          }
        });
    }

    function notifyOfFailure(e) {
      // TODO: incorporate error?
      $translate('default.roles.change.error', {
        profile: getAsString(target),
        role: vm.roleLabels[role],
        name: getAsString(targetDomain),
      }).then(msg => {
        NotificationService.reportError({
          removal: NotificationService.REMOVAL.NEVER,
          message: msg,
        });
      });

      $scope.$evalAsync(() => {
        switch (getCollection(target)) {
          case PersonDomain.COLLECTION:
            vm.membersPersonnelRelations[getId(target)].role = oldRole;
            break;

          case TeamDomain.COLLECTION:
            vm.membersTeamsRelations[getId(target)].role = oldRole;
            break;
          default:
        }
      });
    }
  }


  /*****************************************************************************************************************
   * Presentation Logic
   ****************************************************************************************************************/

  // TODO: change this on when the button is converted to a transform button
  function showAddButton() {
    return vm.toAdd.target && !addingInProgress;
  }

  function showCancelButton() {
    return vm.toAdd.target && !addingInProgress;
  }

  function canEditUser(profileId) {
    const currentUserRole = getCurrentUserRole();
    const currentOrgProfileId = getId(getOrgProfile($ngRedux.getState()));
    if (profileId === currentOrgProfileId && currentUserRole === DEFAULT_ROLES.OWNER) {
      return false; // TODO: enable users editing their role if they are an owner. pending server support
    } else if (!get(vm, 'permissions.canEditAccess')) {
      return false;
    } else if (permission.indexOf('_admin') > -1) {
      return true;
    } else if (!profileId || !currentUserRole || !vm.membersPersonnelRelations[profileId]) {
      return false;
    } else {
      const targetUserRole = vm.membersPersonnelRelations[profileId].role.id;

      return targetUserRole === DEFAULT_ROLES.OWNER
        ? currentUserRole === DEFAULT_ROLES.OWNER
        : true;
    }
  }

  function getCurrentUserRole() {
    const currentOrgProfileId = getId(getOrgProfile($ngRedux.getState()));
    return currentOrgProfileId
      && vm.membersPersonnelRelations
      && vm.membersPersonnelRelations[currentOrgProfileId]
      && vm.membersPersonnelRelations[currentOrgProfileId].role.id;
  }

  /*****************************************************************************************************************
   * Load Data
   ****************************************************************************************************************/

  function configurePublic() {
    return Observable.empty();
  }

  function configurePrivate() {
    return Observable.merge(
      observePermissions(),
      loadRoleLabels(),
      loadMembers(),
    );
  }

  function observePermissions() {
    const orgId = getId(getOrganization($ngRedux.getState()));
    return Observable.fromPromise($permissions.checkOrganizationPermission(orgId, `${permission}_admin`))
      .switchMap(hasPermission => {
        if (hasPermission) {
          permission = `${permission}_admin`;
          return Observable.of({permissions: {canEditAccess: true}});
        } else {
          const VIEW_ACTIONS = {canEditAccess: permission};
          switch (getCollection(targetDomain)) {
            case PROJECT_COLLECTION:
              return rxPermissionsService.userProjectRoleData$
                .takeUntil(eventToObservable($scope, SCOPE_DESTROY))
                .map(rxPermissionsService.projectState(VIEW_ACTIONS));

            case TaskDomain.COLLECTION:
              return rxPermissionsService.buildUserTaskRoleData$(Observable.of(targetDomain))
                .map(rxPermissionsService.taskState(VIEW_ACTIONS));

            case AssetTaskDomain.COLLECTION:
              return rxPermissionsService.buildUserAssetTaskRoleData$(Observable.of(targetDomain), null)
                .map(rxPermissionsService.assetTaskState(VIEW_ACTIONS));
          }
        }
      });
  }

  function cleanupPublic() {

  }

  function cleanupPrivate() {
    // reset the permission
    permission = getEditPermission();
    vm.membersPersonnelRelations = null;
    vm.membersTeamsRelations = null;
    vm.membersPersons = null;
    vm.membersTeams = null;
  }

  function getEditPermission() {
    switch (getCollection(targetDomain)) {
      case PROJECT_COLLECTION:
        return 'project_access_edit';
      case TaskDomain.COLLECTION:
        return 'task_access_edit';
      case AssetTaskDomain.COLLECTION:
        return 'asset_task_access_edit';
    }
  }

  function loadRoleLabels() {
    const roleLabels = {};
    return Promise.all(DEFAULT_ROLES.toList().map(role =>
      $translate(`organizations.roles.${role}.label`).then(label => {
        roleLabels[role] = label;
      })
    )).then(() => ({roleLabels}));
  }

  function loadMembers() {
    // TODO: replace lists with something that can be cleaned up properly
    vm.membersPersonnelRelations = getTargetDomainClass(targetDomain)
      .$getMembersPersonnelRelations(getId(targetDomain));
    vm.membersTeamsRelations = getTargetDomainClass(targetDomain)
      .$getMembersTeamsRelations(getId(targetDomain));

    vm.membersPersonnel = getTargetDomainClass(targetDomain)
      .$getMembersPersonnelList(getId(targetDomain));
    vm.membersTeams = getTargetDomainClass(targetDomain)
      .$getMembersTeamsList(getId(targetDomain));

    return vm.membersTeams.$toObservable()
      .map(list => ({teamCount: list ? Object.keys(list).length : 0}));
  }

  function getTargetDomainClass(target) {
    switch (getCollection(target)) {
      case PROJECT_COLLECTION:
        return ProjectDomain;

      case TaskDomain.COLLECTION:
        return TaskDomain;

      case AssetTaskDomain.COLLECTION:
        return AssetTaskDomain;
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/core-components/manage-access/ManageAccessModalController.js